import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ChevronIcon from "components/icons/chevron-icon";
import CloseIcon from "components/icons/close";
import CheckmarkIcon from "components/icons/checkmark";
import SearchIcon from "components/icons/search-icon";
import { COLORS } from "containers/impact-report/constants";
import {
  SelectContainer,
  SelectButton,
  SelectValue,
  SelectControls,
  ClearButton,
  DropdownMenu,
  SearchInputContainer,
  SearchIconWrapper,
  SearchInput,
  ClearSearchButton,
  DropdownOptions,
  DropdownOption,
  DropdownFooter,
  FooterButton,
  ApplyButton
} from "./CustomSelect.styled";

const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder,
  isClearable,
  isMulti = false,
  showFooter = false,
  isSearchable = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tempSelection, setTempSelection] = useState(isMulti ? [] : null);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle dropdown open/close side effects
  useEffect(() => {
    // Reset search query when dropdown closes
    if (!isOpen) {
      setSearchQuery("");
      return;
    }

    // Initialize temp selection when dropdown opens
    const currentValues = isMulti ? [value].flat().filter(Boolean) : value;
    setTempSelection(currentValues);

    // Focus search input when dropdown opens
    if (isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, value, isMulti, isSearchable]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = option => {
    // With footer: store selection temporarily until user clicks "Apply"
    if (showFooter && isMulti) {
      const isSelected = tempSelection.some(v => v.value === option.value);
      setTempSelection(
        isSelected
          ? tempSelection.filter(v => v.value !== option.value)
          : [...tempSelection, option]
      );
      return;
    }

    if (showFooter) {
      setTempSelection(option);
      return;
    }

    // Without footer: apply selection immediately
    if (isMulti) {
      const currentValues = [value].flat().filter(Boolean);
      const isSelected = currentValues.some(v => v.value === option.value);
      onChange(
        isSelected
          ? currentValues.filter(v => v.value !== option.value)
          : [...currentValues, option]
      );
      return;
    }

    onChange(option);
    setIsOpen(false);
  };

  const handleClear = e => {
    e?.stopPropagation();
    onChange(null);
    setIsOpen(false);
  };

  const handleApply = () => {
    onChange(
      isMulti && tempSelection.length > 0
        ? tempSelection
        : tempSelection || null
    );
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (isMulti) {
      const values = [value].flat().filter(Boolean);
      if (values.length === 0) return placeholder;
      return values.map(v => v.label).join(", ");
    }
    return value?.label || placeholder;
  };

  const isOptionSelected = option => {
    if (showFooter && isMulti) {
      return tempSelection.some(v => v.value === option.value);
    }

    if (showFooter) {
      return tempSelection?.value === option.value;
    }

    if (isMulti) {
      const currentValues = [value].flat().filter(Boolean);
      return currentValues.some(v => v.value === option.value);
    }

    return value?.value === option.value;
  };

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchQuery.trim()) {
      return options;
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );
  }, [isSearchable, searchQuery, options]);

  return (
    <SelectContainer ref={containerRef}>
      <SelectButton
        isOpen={isOpen}
        isHovered={isHovered}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SelectValue
          hasValue={Array.isArray(value) ? value.length > 0 : !!value}
        >
          {getDisplayValue()}
        </SelectValue>
        <SelectControls>
          {isClearable && value && !isMulti && (
            <ClearButton onClick={handleClear}>
              <CloseIcon width={"16"} height={"16"} fill={COLORS.NEUTRAL_500} />
            </ClearButton>
          )}
          <ChevronIcon
            direction={isOpen ? "up" : "down"}
            size={24}
            color={COLORS.NEUTRAL_500}
            fillOpacity={"1"}
          />
        </SelectControls>
      </SelectButton>

      {isOpen && (
        <DropdownMenu>
          {isSearchable && (
            <SearchInputContainer>
              <SearchIconWrapper>
                <SearchIcon size={24} color={COLORS.NEUTRAL_500} />
              </SearchIconWrapper>
              <SearchInput
                ref={searchInputRef}
                type={"text"}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t(
                  "impactReport.communityRequests.searchAgenciesPlaceholder"
                )}
                onClick={e => e.stopPropagation()}
              />
              {searchQuery && (
                <ClearSearchButton onClick={() => setSearchQuery("")}>
                  <CloseIcon fill={COLORS.NEUTRAL_500} />
                </ClearSearchButton>
              )}
            </SearchInputContainer>
          )}
          <DropdownOptions>
            {filteredOptions.map((option, index) => (
              <DropdownOption
                key={option.value}
                isLast={!showFooter && index === filteredOptions.length - 1}
                isSelected={isOptionSelected(option)}
                onClick={() => handleSelect(option)}
              >
                <span>{option.label}</span>
                {(isMulti || showFooter) && isOptionSelected(option) && (
                  <CheckmarkIcon
                    width={"24"}
                    height={"24"}
                    fill={COLORS.LINK_BLUE}
                  />
                )}
              </DropdownOption>
            ))}
            {filteredOptions.length === 0 && (
              <DropdownOption isLast>
                <span>
                  {t("impactReport.communityRequests.noAgenciesFound")}
                </span>
              </DropdownOption>
            )}
          </DropdownOptions>
          {showFooter && (
            <DropdownFooter>
              <FooterButton onClick={handleClear}>
                {t("impactReport.communityRequests.clear")}
              </FooterButton>
              <ApplyButton onClick={handleApply}>
                {t("impactReport.communityRequests.apply")}
              </ApplyButton>
            </DropdownFooter>
          )}
        </DropdownMenu>
      )}
    </SelectContainer>
  );
};

CustomSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  showFooter: PropTypes.bool,
  isSearchable: PropTypes.bool
};

export default CustomSelect;
